
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PaymentStatusData {
  bookingId: string;
  sessionId: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  provider?: string;
}

export const usePaymentStatus = () => {
  const [isProcessing, setIsProcessing] = useState(false);

  const verifyPayment = async (paymentData: PaymentStatusData) => {
    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-payment', {
        body: {
          sessionId: paymentData.sessionId,
          status: paymentData.status,
          provider: paymentData.provider || 'stripe'
        }
      });

      if (error) {
        console.error('Payment verification error:', error);
        toast.error('Failed to verify payment status');
        return false;
      }

      if (data.success) {
        if (paymentData.status === 'completed') {
          toast.success('Payment confirmed successfully!');
        } else if (paymentData.status === 'failed') {
          toast.error('Payment failed. Please try again.');
        } else if (paymentData.status === 'cancelled') {
          toast.info('Payment was cancelled.');
        }
        return true;
      }

      return false;
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error('Failed to verify payment status');
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  // Listen for payment status updates via URL parameters (for redirect handling)
  useEffect(() => {
    const handlePaymentReturn = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const status = urlParams.get('payment_status');
      const bookingId = urlParams.get('booking_id');

      if (sessionId && status && bookingId) {
        console.log('Payment return detected:', { sessionId, status, bookingId });
        
        await verifyPayment({
          bookingId,
          sessionId,
          status: status as PaymentStatusData['status']
        });

        // Clean up URL parameters
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }
    };

    handlePaymentReturn();
  }, []);

  return {
    verifyPayment,
    isProcessing
  };
};
